import { useEffect, useState } from "react";
import { Send, Bell, History } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Campaigns() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [type, setType] = useState("email");
  const [subject, setSubject] = useState("");
  const [messagePt, setMessagePt] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/reminders/pending").then((r) => setReminders(r.data)).catch(() => {});
    api.get("/admin/campaigns").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  const send = async () => {
    if (selected.length === 0) return toast.error("Selecione ao menos uma cliente");
    setSending(true);
    try {
      const { data } = await api.post("/admin/campaigns", { type, subject, message_pt: messagePt, client_ids: selected });
      setResult(data);
      toast.success("Campanha processada!");
      setMessagePt(""); setSubject(""); setSelected([]);
      api.get("/admin/campaigns").then((r) => setHistory(r.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const markSent = async (appointmentId) => {
    await api.post(`/admin/reminders/${appointmentId}/mark-sent`);
    api.get("/admin/reminders/pending").then((r) => setReminders(r.data));
  };

  return (
    <div data-testid="admin-campaigns-page">
      <h1 className="text-2xl font-serif text-[#9B6179] mb-6">Campanhas</h1>
      <Tabs defaultValue="nova">
        <TabsList>
          <TabsTrigger value="nova" data-testid="tab-nova-campanha"><Send className="w-3 h-3 mr-1" /> Nova Campanha</TabsTrigger>
          <TabsTrigger value="lembretes" data-testid="tab-lembretes"><Bell className="w-3 h-3 mr-1" /> Lembretes Pendentes</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico"><History className="w-3 h-3 mr-1" /> Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="nova">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5">
              <p className="text-sm font-medium text-[#9B6179] mb-3">Selecionar Clientes ({selected.length})</p>
              <div className="max-h-64 overflow-y-auto space-y-2 border border-[#9B6179]/10 rounded-[2px] p-3">
                {clients.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm" data-testid={`campaign-client-${c.id}`}>
                    <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                    {c.first_name} {c.last_name} — {c.email}
                  </label>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5 space-y-3">
              <p className="text-sm font-medium text-[#9B6179] mb-1">Mensagem (escreva em Português)</p>
              <div>
                <Label>Tipo de campanha</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="campaign-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="email">E-mail</SelectItem><SelectItem value="whatsapp">WhatsApp (link manual)</SelectItem></SelectContent>
                </Select>
              </div>
              {type === "email" && <div><Label>Assunto</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject-input" /></div>}
              <div><Label>Mensagem (PT)</Label><Textarea rows={5} value={messagePt} onChange={(e) => setMessagePt(e.target.value)} placeholder="Ex: Aproveite 20% de desconto em Peeling Hollywood este mês!" data-testid="campaign-message-input" /></div>
              <p className="text-xs text-[#6F3F55]">A mensagem será traduzida automaticamente para o francês antes do envio.</p>
              <Button disabled={sending || !messagePt} onClick={send} className="w-full bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="campaign-send-button">Enviar Campanha</Button>

              {result && (
                <div className="mt-4 space-y-2" data-testid="campaign-result">
                  <p className="text-xs font-medium text-[#9B6179]">Resultado:</p>
                  {result.results.map((r) => (
                    <div key={r.client_id} className="flex justify-between items-center text-xs border-b border-[#9B6179]/5 py-1.5">
                      <span>{r.client_name}</span>
                      {r.wa_link ? (
                        <a href={r.wa_link} target="_blank" rel="noopener noreferrer" data-testid={`campaign-wa-link-${r.client_id}`} className="text-[#25D366] font-medium">Enviar WhatsApp</a>
                      ) : (
                        <span className={r.status === "sent" ? "text-green-600" : "text-red-600"}>{r.status}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="lembretes">
          <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5 space-y-3">
            {reminders.length === 0 && <p className="text-sm text-[#6F3F55]">Nenhum lembrete pendente.</p>}
            {reminders.map((r) => (
              <div key={r.appointment_id} className="flex justify-between items-center border-b border-[#9B6179]/5 py-3" data-testid={`reminder-${r.appointment_id}`}>
                <div>
                  <p className="text-sm font-medium text-[#9B6179]">{r.client_name} — {r.service_name} {r.is_laser && "🔴 Laser"}</p>
                  <p className="text-xs text-[#6F3F55]">{r.date} às {r.slot}</p>
                </div>
                <div className="flex gap-2">
                  <a href={r.wa_link} target="_blank" rel="noopener noreferrer" data-testid={`reminder-wa-link-${r.appointment_id}`}><Button size="sm" className="bg-[#25D366] text-white">Enviar WhatsApp</Button></a>
                  <Button size="sm" variant="outline" onClick={() => markSent(r.appointment_id)} data-testid={`reminder-mark-sent-${r.appointment_id}`}>Marcar enviado</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="historico">
          <div className="bg-white rounded-[2px] p-6 shadow-sm border border-[#9B6179]/5 space-y-3">
            {history.length === 0 && <p className="text-sm text-[#6F3F55]">Nenhuma campanha enviada ainda.</p>}
            {history.map((h) => (
              <div key={h.id} className="border-b border-[#9B6179]/5 py-2 text-sm" data-testid={`campaign-history-${h.id}`}>
                <span className="font-medium text-[#9B6179] capitalize">{h.type}</span> — {h.client_ids.length} clientes — {new Date(h.created_at).toLocaleDateString()}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
